import { useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from './error-copy.js'
import { hasDraggedFiles, isInsideSidebar } from './file-drag.js'
import { uploadFile } from './upload-client.js'
import {
  type UploadItem,
  type UploadTarget,
  clearFinishedUploads,
  createUploadItems,
  patchUploadItem,
  removeUploadItem,
  summarizeUploads,
} from './upload-items.js'
import { MAX_CONCURRENT_FILE_UPLOADS, type QueueHandle, createUploadQueue } from './upload-queue.js'

/** 完了・中断した項目が進捗パネルから自動で消えるまでの猶予 */
const AUTO_DISMISS_MS = 3000

type UploadActions = {
  enqueue: (target: UploadTarget, files: FileList | readonly File[]) => void
  dismiss: (id: string) => void
  clearFinished: () => void
  /** 画面のどこに落としても入る先。プロジェクト詳細が自分を登録する */
  setPageDropTarget: (target: UploadTarget | null) => void
}

type UploadState = {
  items: readonly UploadItem[]
  pageDropTarget: UploadTarget | null
  /** ファイルを画面上でドラッグ中か（メイン領域のオーバーレイ表示用） */
  isDraggingFiles: boolean
}

// 進捗は送信中に何度も更新される。操作だけ使う側（サイドバーの各行）まで再描画しないよう分ける
const UploadActionsContext = createContext<UploadActions | null>(null)
const UploadStateContext = createContext<UploadState | null>(null)

function newUploadId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * アップロードの管理役。アプリ全体で 1 つだけ置く。
 *
 * どの画面から、どのプロジェクトへ上げても同じキューで処理し、進捗は右下のパネルに
 * まとめて出す。画面を移動しても続く。
 *
 * ファイルのドロップはここにある window のリスナーだけが受け付ける。画面ごとに
 * window へ付けると登録順で挙動が変わり、サイドバーと本文の両方に上がるなどの
 * 事故になるため。
 */
export function UploadManagerProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<UploadItem[]>([])
  const [pageDropTarget, setPageDropTarget] = useState<UploadTarget | null>(null)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)

  const itemsRef = useRef(items)
  itemsRef.current = items
  const pageDropTargetRef = useRef(pageDropTarget)
  pageDropTargetRef.current = pageDropTarget

  const queue = useRef(createUploadQueue(MAX_CONCURRENT_FILE_UPLOADS))
  const abortControllers = useRef(new Map<string, AbortController>())
  const queueHandles = useRef(new Map<string, QueueHandle>())
  const dismissTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const update = useCallback((id: string, patch: Partial<Omit<UploadItem, 'id'>>) => {
    setItems((prev) => patchUploadItem(prev, id, patch))
  }, [])

  const forget = useCallback((id: string) => {
    abortControllers.current.delete(id)
    queueHandles.current.delete(id)
    const timer = dismissTimers.current.get(id)
    if (timer) clearTimeout(timer)
    dismissTimers.current.delete(id)
    setItems((prev) => removeUploadItem(prev, id))
  }, [])

  const runUpload = useCallback(
    async (item: UploadItem, signal: AbortSignal) => {
      update(item.id, { status: 'uploading' })
      try {
        await uploadFile(item.projectId, item.file, {
          signal,
          onProgress: (p) =>
            update(item.id, { loadedBytes: p.loadedBytes, totalBytes: p.totalBytes }),
        })
        update(item.id, { status: 'done', loadedBytes: item.file.size, totalBytes: item.file.size })
      } catch (err) {
        // ✕ で中断した場合はエラーではない
        if (signal.aborted) {
          update(item.id, { status: 'cancelled' })
          return
        }
        update(item.id, { status: 'error', error: toUserMessage(err, SUBMIT_ERROR_FALLBACK) })
      } finally {
        abortControllers.current.delete(item.id)
        queueHandles.current.delete(item.id)
        void queryClient.invalidateQueries({ queryKey: ['files', item.projectId] })
      }
    },
    [queryClient, update],
  )

  const enqueue = useCallback<UploadActions['enqueue']>(
    (target, files) => {
      const created = createUploadItems(target, Array.from(files), newUploadId)
      if (created.length === 0) return
      setItems((prev) => [...prev, ...created])
      for (const it of created) {
        // 待機中のうちから用意しておき、開始直後のレースでも中断できるようにする
        const controller = new AbortController()
        abortControllers.current.set(it.id, controller)
        const handle = queue.current.enqueue(
          async () => {
            if (controller.signal.aborted) {
              update(it.id, { status: 'cancelled' })
              abortControllers.current.delete(it.id)
              queueHandles.current.delete(it.id)
              return
            }
            await runUpload(it, controller.signal)
          },
          (error) =>
            update(it.id, { status: 'error', error: toUserMessage(error, SUBMIT_ERROR_FALLBACK) }),
        )
        queueHandles.current.set(it.id, handle)
      }
    },
    [runUpload, update],
  )

  /**
   * ✕ の動作:
   * - 待機中ならキューから外す（まだ upload-url を発行していないのでサーバ側の後始末は不要）
   * - 開始直後のレースで外せなかった場合と、送信中は中断する（状態は runUpload 側で倒す）
   * - 結果が出たものは表示から消す
   */
  const dismiss = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id)
      if (item?.status === 'queued' && queueHandles.current.get(id)?.cancel()) {
        abortControllers.current.delete(id)
        queueHandles.current.delete(id)
        update(id, { status: 'cancelled' })
        return
      }
      if (item?.status === 'uploading' || item?.status === 'queued') {
        const controller = abortControllers.current.get(id)
        if (controller && !controller.signal.aborted) controller.abort()
        return
      }
      forget(id)
    },
    [forget, update],
  )

  const clearFinished = useCallback(() => {
    setItems((prev) => clearFinishedUploads(prev))
  }, [])

  // 完了・中断した項目を一定時間後に消す。項目ごとに一度だけタイマーを張る。
  // items の変化のたびに張り直すと、他のファイルの進捗更新でタイマーがリセットされ続け、
  // 先に終わったファイルがいつまでも消えなくなる。
  useEffect(() => {
    for (const it of items) {
      if (
        (it.status === 'done' || it.status === 'cancelled') &&
        !dismissTimers.current.has(it.id)
      ) {
        dismissTimers.current.set(
          it.id,
          setTimeout(() => forget(it.id), AUTO_DISMISS_MS),
        )
      }
    }
  }, [items, forget])

  // 送信中・待機中にタブを閉じたりリロードしたりしたら確認を出す
  const { pending } = summarizeUploads(items)
  useEffect(() => {
    if (pending === 0) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Chrome 系では returnValue の代入が必要（文言はブラウザ標準に置き換わる）
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [pending])

  useWindowFileDrop({ enqueue, pageDropTargetRef, setIsDraggingFiles })

  // ログアウト等で管理役ごと消えるときは、未開始の分を捨てる（送信中はそのまま続ける）
  useEffect(() => {
    const q = queue.current
    const timers = dismissTimers.current
    return () => {
      q.clearPending()
      for (const t of timers.values()) clearTimeout(t)
    }
  }, [])

  const actions = useMemo<UploadActions>(
    () => ({ enqueue, dismiss, clearFinished, setPageDropTarget }),
    [enqueue, dismiss, clearFinished],
  )
  const state = useMemo<UploadState>(
    () => ({ items, pageDropTarget, isDraggingFiles }),
    [items, pageDropTarget, isDraggingFiles],
  )

  return (
    <UploadActionsContext.Provider value={actions}>
      <UploadStateContext.Provider value={state}>{children}</UploadStateContext.Provider>
    </UploadActionsContext.Provider>
  )
}

/**
 * 画面全体でのファイルのドラッグ & ドロップ。
 *
 * - サイドバーのプロジェクト行は自分で受け取り、preventDefault で「処理済み」を示す。
 *   ここは処理済みなら上げずに、ドラッグ中の表示だけを戻す（stopPropagation すると
 *   ここまで届かず、オーバーレイが消えなくなる）
 * - サイドバーのそれ以外の場所は受け付けない（カーソルを「置けない」にする）
 * - それ以外は、開いているプロジェクトがあればそこへ上げる
 * - どこにも入らない場合もブラウザ既定の「ファイルを開く」は止める。止めないと、
 *   落とし損ねただけでアプリから離れてしまう
 */
function useWindowFileDrop({
  enqueue,
  pageDropTargetRef,
  setIsDraggingFiles,
}: {
  enqueue: UploadActions['enqueue']
  pageDropTargetRef: { current: UploadTarget | null }
  setIsDraggingFiles: (value: boolean) => void
}) {
  useEffect(() => {
    // dragenter / dragleave は子要素の境界ごとに発火するので深さで数える
    let depth = 0

    const onDragEnter = (e: DragEvent) => {
      if (!hasDraggedFiles(e.dataTransfer)) return
      depth += 1
      if (depth === 1) setIsDraggingFiles(true)
    }
    const onDragLeave = (e: DragEvent) => {
      if (!hasDraggedFiles(e.dataTransfer)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) setIsDraggingFiles(false)
    }
    const onDragOver = (e: DragEvent) => {
      if (!hasDraggedFiles(e.dataTransfer)) return
      // 手前の受け口（サイドバーのプロジェクト行）が既に受け付けていれば任せる
      if (e.defaultPrevented) return
      e.preventDefault()
      if (!e.dataTransfer) return
      const accepts = pageDropTargetRef.current !== null && !isInsideSidebar(e.target)
      e.dataTransfer.dropEffect = accepts ? 'copy' : 'none'
    }
    const onDrop = (e: DragEvent) => {
      if (!hasDraggedFiles(e.dataTransfer)) return
      // 手前の受け口が preventDefault 済みなら、そちらが既に上げている
      const handledByTarget = e.defaultPrevented
      e.preventDefault()
      // ドロップ時は dragleave が来ないので、受け口が処理した場合もここで戻す
      depth = 0
      setIsDraggingFiles(false)
      if (handledByTarget) return
      const target = pageDropTargetRef.current
      if (!target || isInsideSidebar(e.target)) return
      if (e.dataTransfer && e.dataTransfer.files.length > 0) enqueue(target, e.dataTransfer.files)
    }

    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('drop', onDrop)
    }
  }, [enqueue, pageDropTargetRef, setIsDraggingFiles])
}

export function useUploadActions(): UploadActions {
  const ctx = useContext(UploadActionsContext)
  if (!ctx) throw new Error('useUploadActions は UploadManagerProvider の内側で使ってください')
  return ctx
}

export function useUploadState(): UploadState {
  const ctx = useContext(UploadStateContext)
  if (!ctx) throw new Error('useUploadState は UploadManagerProvider の内側で使ってください')
  return ctx
}

/**
 * 「画面のどこに落としてもこのプロジェクトへ」を登録する。プロジェクト詳細で使う。
 * 画面を離れると解除される。
 */
export function usePageDropTarget(target: UploadTarget): void {
  const { setPageDropTarget } = useUploadActions()
  const { projectId, projectName } = target
  useEffect(() => {
    setPageDropTarget({ projectId, projectName })
    return () => setPageDropTarget(null)
  }, [projectId, projectName, setPageDropTarget])
}
